import {
  renderDocFooter,
  renderNotFound,
  renderPager,
  renderShell,
  renderSidebarNav,
  renderStubNotice,
  renderToc,
} from './components/shell';
import { getPage } from './lib/content';
import { extractHeadings, renderMarkdown } from './lib/markdown';
import { adjacentPages, findNavItem, SITE } from './lib/nav';
import { initRouter, navigate } from './lib/router';

export function boot(): void {
  const root = document.querySelector<HTMLDivElement>('#app');
  if (!root) return;

  root.innerHTML = renderShell();

  const sidebar = document.querySelector('#sidebar');
  const sidebarNav = document.querySelector('#sidebar-nav');
  const docRoot = document.querySelector('#doc-root');
  const toc = document.querySelector('#toc');
  const backdrop = document.querySelector('.backdrop');
  const searchInput = document.querySelector<HTMLInputElement>('#wiki-search');

  if (!sidebar || !sidebarNav || !docRoot || !toc || !backdrop) return;

  const setNavOpen = (open: boolean) => {
    sidebar.classList.toggle('is-open', open);
    backdrop.toggleAttribute('hidden', !open);
    document.body.classList.toggle('nav-open', open);
  };

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const action = target?.closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'open-nav') setNavOpen(true);
    if (action === 'close-nav') setNavOpen(false);
  });

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim().toLowerCase();
    sidebarNav.querySelectorAll<HTMLAnchorElement>('.nav-link').forEach((link) => {
      const text = link.textContent?.toLowerCase() ?? '';
      const match = !query || text.includes(query);
      link.parentElement?.toggleAttribute('hidden', !match);
    });
  });

  initRouter((slug, hash) => {
    setNavOpen(false);
    sidebarNav.innerHTML = renderSidebarNav(slug);

    const page = getPage(slug);
    const navItem = findNavItem(slug);

    if (!page) {
      docRoot.innerHTML = renderNotFound(slug);
      toc.innerHTML = '';
      document.title = `Not found · ${SITE.name}`;
      return;
    }

    const { prev, next } = adjacentPages(slug);
    const headings = extractHeadings(page.markdown);
    const bodyHtml = renderMarkdown(page.markdown);
    const stubBanner = navItem?.stub ? renderStubNotice(page.meta.title) : '';

    docRoot.innerHTML = `
      <div class="doc-header">
        ${page.meta.eyebrow ? `<p class="eyebrow">${page.meta.eyebrow}</p>` : ''}
        <h1>${page.meta.title}</h1>
        ${page.meta.description ? `<p class="lede">${page.meta.description}</p>` : ''}
      </div>
      ${stubBanner}
      <div class="doc-body">${bodyHtml}</div>
      ${renderPager(prev, next)}
      ${renderDocFooter()}
    `;

    toc.innerHTML = renderToc(headings);
    document.title = `${page.meta.title} · ${SITE.name}`;

    const descriptionMeta = ensureMeta('description');
    descriptionMeta.setAttribute('content', page.meta.description);

    window.scrollTo({ top: 0 });

    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView();
      });
    }
  });

  if (window.location.pathname === '/' || window.location.pathname === '') {
    navigate(SITE.homeSlug, { replace: true });
  }
}

function ensureMeta(name: string): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  return meta;
}
